/**
 * The durable-queue health verdict, rendered. Extracted from Customers.tsx so
 * Phibian Console's System Health section shows the SAME reading, not a second
 * interpretation of the same body. Every branch of `JobsHealthReading` has its
 * own sentence; none of them is a blank.
 */
import type { JobsHealthReading } from '../platform/jobsHealth.js';

/** `null` is "the read has not answered", which is not "the queues are fine". */
export function JobsHealthPanel({ health }: { health: JobsHealthReading | null }) {
  return (
    <section>
      <h2>Platform queue health</h2>
      {health === null ? (
        <p className="quiet">Reading…</p>
      ) : health.kind === 'unreadable' ? (
        <div className="banner" role="alert">
          <strong>The platform queue health read did not answer.</strong> {health.message}
          <p className="quiet">
            This is not a healthy queue and it is not an unhealthy one — it is a read that did not
            complete.
          </p>
        </div>
      ) : health.kind === 'malformed' ? (
        <div className="banner" role="alert">
          <strong>The reply carried no status field.</strong>
          <p className="quiet">
            This route always answers 200 with its verdict in the body, so a body without one is a
            surprise rather than a queue state. It is not being read as healthy.
          </p>
        </div>
      ) : health.kind === 'withheld' ? (
        <div className="banner" role="alert">
          <strong>The durable queue probe did not run — {health.reason.replace(/_/g, ' ')}.</strong>
          <p className="quiet">
            No counts were taken, so none are shown. Nothing here says the queues are fine; it says
            we did not ask.
          </p>
        </div>
      ) : health.kind === 'unhealthy' ? (
        <div className="banner" role="alert">
          <strong>Durable job work is not healthy — {health.status}.</strong>
          {health.executorFault ? (
            <p>
              This instance&apos;s executor is <strong>{health.executorFault}</strong>. That is a
              deployment fault, not a fault of any one queue.
            </p>
          ) : null}
          {health.failures.length > 0 ? (
            <ul>
              {health.failures.map((failure) => (
                <li key={failure.queue}>
                  <code>{failure.queue}</code> — <strong>{failure.disposition}</strong>
                  {failure.ready === null ? (
                    <span className="quiet"> · backlog withheld</span>
                  ) : (
                    <span className="quiet"> · backlog {failure.ready}</span>
                  )}
                  {failure.unexpectedFailuresInWindow === null ? null : (
                    <span className="quiet">
                      {' '}
                      · {failure.unexpectedFailuresInWindow} unexpected failures in the window
                    </span>
                  )}
                  {failure.oldestAgeSeconds === null ? null : (
                    <span className="quiet"> · oldest {failure.oldestAgeSeconds}s</span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : health.kind === 'paused' ? (
        <p>
          <code>GET /api/system/jobs/health</code> → <strong>{health.status}</strong>. Claiming is
          deliberately paused on {health.queues.join(', ')} for a bounded deploy handoff — worth
          knowing, not an incident.
        </p>
      ) : health.kind === 'unknown' ? (
        <p>
          <code>GET /api/system/jobs/health</code> → <strong>{health.status}</strong>. No verdict
          for {health.queues.join(', ')} — the probe could not vouch for that window. Not a failure,
          and not an all-clear.
        </p>
      ) : (
        <p>
          <code>GET /api/system/jobs/health</code> → <strong>{health.status}</strong>. All{' '}
          {health.queuesObserved} durable queues accounted for and healthy.
        </p>
      )}
    </section>
  );
}
